import React from 'react';
import {alpha, Box, styled} from '@mui/material';
import {
  Body,
  Checkbox,
  designSystemToken,
  Label,
  Tooltip,
} from '@lightricks/react-design-system';

const checkboxCheckedSrc = '/assets/svg/checkbox-checked.svg';
const checkboxUncheckedSrc = '/assets/svg/checkbox-unchecked.svg';
const checkboxUncheckedHoverSrc = '/assets/svg/checkbox-unchecked-hover.svg';

const getBorderColor = (selected: boolean, disabled: boolean) => {
  if (disabled) {
    return alpha(designSystemToken('semantic.bg.disabled-secondary'), 0.1);
  }
  return selected
    ? designSystemToken('semantic.fg.primary')
    : alpha(designSystemToken('semantic.bg.neutral-secondary'), 0.08);
};

const getBackgroundColor = (hovered: boolean, disabled: boolean) => {
  if (disabled) {
    return designSystemToken('semantic.bg.secondary');
  }
  return hovered
    ? alpha(designSystemToken('comp.button.bg.neutral.plain.hover'), 0.2)
    : designSystemToken('semantic.bg.primary');
};

const EditorCheckboxCellContainer = styled(Box)(
  ({
    disabled,
    selected,
    hovered,
  }: {
    disabled: boolean;
    selected: boolean;
    hovered: boolean;
  }) => ({
    background: getBackgroundColor(hovered, disabled),
    borderRadius: '16px',
    border: `1px solid ${getBorderColor(selected, disabled)}`,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px 16px',
    minHeight: 0,
    minWidth: 0,
    alignContent: 'flex-start',
    gap: '6px',
    cursor: disabled ? 'default' : 'pointer',
    transition: 'all 0.2s ease-in-out',
  })
);

const UncheckedCheckboxIconWrapper = styled(Box)({
  'position': 'relative',
  'width': '24px',
  'height': '24px',
  '& img': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    transition: 'opacity 0.2s ease-in-out',
    willChange: 'opacity',
    pointerEvents: 'none',
  },
});

const CheckboxWrapper = styled(Box)({
  position: 'absolute',
  left: '8px',
  top: '8px',
  zIndex: 1,
});

function UncheckedCheckboxIcon({
  isHovered,
  isChecked,
  isDisabled,
}: {
  isHovered: boolean;
  isChecked: boolean;
  isDisabled: boolean;
}) {
  return (
    <UncheckedCheckboxIconWrapper>
      <img
        src={checkboxUncheckedSrc}
        alt=""
        style={{
          opacity: isHovered && !isChecked && !isDisabled ? 0 : 1,
        }}
      />
      <img
        src={checkboxUncheckedHoverSrc}
        alt=""
        style={{
          opacity: isHovered && !isChecked && !isDisabled ? 1 : 0,
        }}
      />
    </UncheckedCheckboxIconWrapper>
  );
}

interface CheckboxContainerProps {
  isChecked: boolean;
  isHovered: boolean;
  isDisabled: boolean;
  onChange: () => void;
}

function CheckboxContainer({
  isChecked,
  isHovered,
  isDisabled,
  onChange,
}: CheckboxContainerProps) {
  return (
    <CheckboxWrapper>
      <Checkbox
        checked={isChecked}
        disabled={isDisabled}
        customCheckedIcon={<img src={checkboxCheckedSrc} alt="" />}
        customUncheckedIcon={
          <UncheckedCheckboxIcon
            isHovered={isHovered}
            isChecked={isChecked}
            isDisabled={isDisabled}
          />
        }
        onChange={onChange}
      />
    </CheckboxWrapper>
  );
}

interface EditorCheckboxCellProps {
  isSelected: boolean;
  onClick: (checked: boolean) => void;
  icon: React.ReactNode;
  label: string;
  subtitle?: string | null;
  badgeContent?: string | null;
  description: string;
  isDisabled: boolean;
  tooltip: string;
}

function EditorCheckboxCell({
  isSelected,
  onClick,
  icon,
  label,
  subtitle,
  badgeContent,
  description,
  isDisabled,
  tooltip,
}: EditorCheckboxCellProps) {
  const [isHovered, setIsHovered] = React.useState(false);
  const iconRef = React.useRef<HTMLDivElement>(null);

  const handleClick = () => {
    if (isDisabled) {
      return;
    }
    onClick(!isSelected);
  };

  const handleMouseEnter = () => {
    if (!isDisabled) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const containerContent = (
    <EditorCheckboxCellContainer
      disabled={isDisabled}
      selected={isSelected}
      hovered={isHovered}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <CheckboxContainer
        isChecked={isSelected}
        isHovered={isHovered}
        isDisabled={isDisabled}
        onChange={() => {}}
      />
      {badgeContent && (
        <Box
          sx={{
            position: 'absolute',
            right: '11px',
            top: '11px',
            display: 'flex',
            gap: '10px',
            padding: '2px 3px',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '6px',
          }}
        >
          <Label size="md" color={designSystemToken('semantic.fg.white')}>
            {badgeContent}
          </Label>
        </Box>
      )}
      <Box ref={iconRef}>{icon}</Box>
      <Body
        size="lg"
        color={
          isDisabled
            ? designSystemToken('semantic.fg.disabled')
            : designSystemToken('semantic.fg.primary')
        }
      >
        {label}
      </Body>
      {subtitle && (
        <Body
          size="sm"
          fontWeight="700"
          color={
            isDisabled
              ? designSystemToken('semantic.fg.disabled')
              : designSystemToken('semantic.fg.primary')
          }
        >
          {subtitle}
        </Body>
      )}
      <Body
        size="sm"
        color={
          isDisabled
            ? designSystemToken('semantic.fg.disabled')
            : designSystemToken('semantic.fg.secondary')
        }
        sx={{
          width: '100%',
          wordBreak: 'break-word',
          textAlign: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {description}
      </Body>
    </EditorCheckboxCellContainer>
  );

  if (isDisabled) {
    return (
      <Tooltip
        title={tooltip}
        arrow
        type="light"
        placement="top"
        PopperProps={{
          anchorEl: iconRef.current,
        }}
        sx={{
          width: '179px',
          padding: '10px 12px',
          textAlign: 'center',
          fontSize: '14px',
          gap: '0px',
        }}
      >
        {containerContent}
      </Tooltip>
    );
  }

  return containerContent;
}

export default EditorCheckboxCell;
